import type { EntitlementKey } from '../../services/feature-flag-store.service';
import type { PermissionRequirement } from '../../auth/permission.guard';
/**
 * Static config for the Settings section pages (`SettingsSectionPage`). Kept in its own
 * file (no component import) so `app.routes.ts` can reference it in route `data` without pulling
 * the lazily-loaded component into the eager bundle.
 *
 * Tab gates mirror the child routes' `canMatch` guards in `app.routes.ts` — keep the two in sync
 * when adding a tab: an `entitlement` here pairs with `entitlementGuard(key)` on the child route.
 * The `featureFlag` field that used to sit beside it was deleted in the 2026-09-21 feature-flag
 * audit along with the build-time service it named; the two tabs that had grown one (Approvals and
 * Enforcement, from two independent findings that reached the same conclusion from opposite ends
 * of the app) carry the equivalent per-org entitlement instead. Admin gating lives on the
 * SECTION route (and the sidebar item), not per tab: within Organization and AI every tab is
 * admin-only, and Connectivity has none.
 */
export interface SettingsSectionTab {
  label: string;
  /** Absolute route of the tab's child page. */
  route: string;
  /**
   * When set, the tab is only rendered when the caller's org has this per-org entitlement.
   * Discoverability only — the child route's `entitlementGuard` is the real frontend gate, and the
   * backend independently 404s its API for an unentitled org.
   */
  entitlement?: EntitlementKey;
  /** Mark the tab active only on an exact URL match (needed when a sibling tab's route extends this one). */
  exact?: boolean;
  /** Global organization permission requirement for discovering this tab. */
  permissions?: PermissionRequirement;
}

export interface SettingsSectionConfig {
  title: string;
  icon: string;
  description: string;
  tabs: SettingsSectionTab[];
}

/** Route `data` key the section page reads its config from. */
export const SETTINGS_SECTION_DATA_KEY = 'settingsSection';

// Route stays /settings/organization (only the display name changed 2026-08-19), so bookmarks
// and the /admin/users|teams redirects keep working.
export const ORGANIZATION_SECTION: SettingsSectionConfig = {
  title: 'Users & Teams',
  icon: 'groups',
  description: 'Manage your organization’s users and teams, and who may approve exceptions.',
  tabs: [
    { label: 'Users', route: '/settings/organization/users', permissions: { allOf: ['member.read'] } },
    { label: 'Teams', route: '/settings/organization/teams', entitlement: 'teams', permissions: { allOf: ['team.read'] } },
    // Admin nomination of who may approve a PR/hook block or a developer exception request
    // (chatops-approvals-plan Task 4, D-A3(b)/D-A4). Ungated by entitlement -- the section's own
    // Each tab has its own typed permission requirement, and ApproverPolicyComponent additionally
    // gates itself internally so the same component is safe to embed on a mixed-audience page.
    // `entitlement: 'approval'` (Finding I1) is the same per-org value the backend enforces (no
    // deployment property caps it since 2026-09-24) and mirrors the child route's own
    // `canMatch` guard -- without it, the tab rendered unconditionally even while the whole
    // surface 404s. It is an ENTITLEMENT rather than the section-wide kind the comment above
    // disclaims: it gates the feature, not the typed permission requirement declared on the tab.
    //
    // Labelled **Approvers** since 2026-09-24 (navigation IA): it is the org-wide nomination editor,
    // not the Approvals & exceptions inbox (`/approvals`, under Security operations). The workspace settings
    // dialog's tab for the same editor at workspace scope is "Workspace approvers". The route keeps
    // its historical `approvals` segment so bookmarks survive.
    { label: 'Approvers', route: '/settings/organization/approvals', entitlement: 'approval', permissions: { allOf: ['approval.policy.manage'] } },
  ],
};

/**
 * **Organization** — the read-only page that shows which licence plan the organization is on and
 * what it includes (licence plan Task 14, owner request 2026-10-05). A separate section from
 * {@link ORGANIZATION_SECTION}, which keeps the historical `organization` id and routes for Users &
 * Teams. Every role holds `organization.read`, so it is not admin-only; the page changes nothing,
 * because plans are assigned by Phoenix staff.
 */
export const ORGANIZATION_PLAN_SECTION: SettingsSectionConfig = {
  title: 'Organization',
  icon: 'business',
  description: 'See which plan your organization is on and what it includes.',
  tabs: [
    { label: 'Plan', route: '/settings/plan', exact: true, permissions: { allOf: ['organization.read'] } },
    // Read-only organization switches (DAST ownership acknowledgement state). Same permission as Plan.
    { label: 'Settings', route: '/settings/plan/settings', permissions: { allOf: ['organization.read'] } },
  ],
};

/*
 * The former **General** section (Memory / Remediation Guidance / Enforcement) was retired on
 * 2026-09-24 (navigation IA). Each tab kept its component and its gates and moved to a working
 * home first: Memory is a tab of AI below (`repoMemory`), Remediation guidance is its own admin
 * page under the sidebar's Settings section (`/settings/remediation`, no entitlement), and the two enforcement toggles are the Requirements tab of the Enforcement
 * page (`/enforcement/requirements`). Every `/settings/general/**` URL redirects — see
 * `app.routes.ts`.
 */

/**
 * AI — providers, spend and hunt tuning. Every tab is admin-only (the section route's roleGuard).
 *
 * "AI providers & usage" was "AI Configurations" until 2026-09-24: it configures model providers,
 * keys and spend, and the old name was easy to confuse with **AI Agent Config** (Policies &
 * Controls), which certifies the safety of AI-assistant configuration files in repositories.
 * Memory moved here from the retired General section; it keeps the `repoMemory` entitlement on
 * the tab, mirrored by its child route's `canMatch`.
 */
export const AI_SECTION: SettingsSectionConfig = {
  title: 'AI',
  icon: 'smart_toy',
  description: 'Configure AI providers and usage, tune hunt deduplication, and review org memory.',
  tabs: [
    { label: 'AI providers & usage', route: '/settings/ai/configuration', permissions: { anyOf: ['ai.configure', 'ai.spend.manage'] } },
    { label: 'Hunt Deduplication', route: '/settings/ai/hunt-dedup', permissions: { allOf: ['hunt.configure'] } },
    { label: 'Memory', route: '/settings/ai/memory', entitlement: 'repoMemory', permissions: { allOf: ['ai.memory.manage'] } },
  ],
};

export const CONNECTIVITY_SECTION: SettingsSectionConfig = {
  title: 'Connectivity',
  icon: 'hub',
  description:
    'Connect source control (cloud or self-hosted), manage API keys, and connect Slack for approvals.',
  tabs: [
    // Integrations is the single place to set up source control, including self-hosted SCM: its
    // "Self-hosted SCM" section carries the connector-box vs reverse-proxy comparison and the
    // connector boxes. The separate On-prem SCM tab (2026-09-24) was folded into it on
    // 2026-09-25; `/settings/connectivity/onprem-scm` redirects to that section.
    { label: 'Integrations', route: '/settings/connectivity/integrations', permissions: { allOf: ['integration.read'] } },
    // Every bundle holds `api_key.gate.self_manage` (personal hook keys, D18): a member reaches their own keys here
    // even without the admin-only org list permission. The route itself is open to all authenticated users.
    { label: 'API Keys', route: '/settings/connectivity/api-keys', permissions: { anyOf: ['api_key.metadata.read', 'api_key.gate.self_manage'] } },
    // { label: 'Backend', route: '/settings/connectivity/backend' },
    // "Chat approvals" (was "Comms" until 2026-09-25): the page connects Slack so approvals can be
    // answered in chat and links the caller's own Slack account — it does not manage general
    // notifications, and the old label implied it did. Route and tour id unchanged.
    // `entitlement: 'chatops'` (2026-09-25): the tab used to render for every org, and for an org
    // without the entitlement it opened a blank page (the component renders nothing when the
    // entitlement is off). Paired with the child route's `entitlementGuard('chatops')`; the
    // backend independently 404s `/api/v1/chatops/**` (ChatOpsSettingsController).
    { label: 'Chat approvals', route: '/settings/connectivity/comms', entitlement: 'chatops', permissions: { anyOf: ['chat_identity.link.self', 'notification.read'] } },
  ],
};

/**
 * **Developer setup** (named AI-IDE-Integration from 2026-08-26 until 2026-09-24; the route keeps
 * `/settings/ai-ide-integration`) — the customer-facing install surface for everything Purple runs
 * on the developer's own machine or CI: skills, MCP servers, editor adapters, session hooks and CI
 * integrations. The on-prem connectors moved to Connectivity on 2026-09-24 and now live in the
 * Integrations tab's Self-hosted SCM section; the old `connectors` URL redirects there. The CI/CD and Library & Container CI tabs are now one
 * **CI integrations** page with two headed sections; the old `sbom-ci` URL redirects to the second
 * section's anchor.
 *
 * Unlike the other four sections this one is NOT admin-only: installing the client packages is a
 * developer action, and the packages carry no tenant data (they are org-agnostic templates —
 * the connector box on the Connectors tab keeps its own org-scoped download path). Visibility is
 * gated by the global `aiIdeIntegration` capability flag on the sidebar item and by the
 * matching backend flag on `/api/v1/install-packs`, which 404s when it is down.
 */
export const AI_IDE_INTEGRATION_SECTION: SettingsSectionConfig = {
  title: 'Developer setup',
  icon: 'integration_instructions',
  description:
    'Download and install everything Phoenix Purple runs on your side — in your editor and in CI.',
  tabs: [
    { label: 'Get Started', route: '/settings/ai-ide-integration/start' },
    { label: 'Skills', route: '/settings/ai-ide-integration/skills' },
    { label: 'MCP', route: '/settings/ai-ide-integration/mcp' },
    { label: 'IDEs & Plugins', route: '/settings/ai-ide-integration/ide' },
    { label: 'Hooks', route: '/settings/ai-ide-integration/hooks' },
    { label: 'CI integrations', route: '/settings/ai-ide-integration/ci' },
  ],
};

/**
 * **Scanning** (2026-09-12 as "Scan policy"; titled Scanning since 2026-09-25) — the org-wide
 * answer to "how does a repository get scanned", in one place. Reached from the sidebar's
 * **Settings → Scanning** entry: it sat under Security operations from 2026-09-24 and returned to
 * Settings on 2026-09-25. The `/settings/scan-policy` route is unchanged, so its tours, the legacy
 * `/policies` / `/schedules` / `/settings/scan-configuration` redirects and every bookmark keep
 * working.
 *
 * Both tabs moved here from RISKS, where they did not belong: RISKS is where FINDING LISTS live,
 * and neither of these lists a finding. PR Policy decides a pull request's fate; Scheduled scans
 * decides what runs on a cadence. They are configuration, and a user looking for either one looks
 * under Settings.
 *
 * **Not admin-only**, and that is a change from how both entries shipped. Reads on both pages are
 * useful and safe for an ordinary org member — a developer can see which policy governs their
 * repository and which schedules scan it — while every WRITE is already admin-gated at the
 * endpoint behind `phx.policy.require-admin-for-writes`, and PR Policy's Bindings tab reports its
 * own 403 as an access boundary rather than as a malfunction. Hiding the section would have hidden
 * a useful read to protect a write that is already protected.
 *
 * The ENTITLEMENT gate sits on the PR Policy TAB, not on the section: `/api/v1/scan-policies` 404s
 * for a non-entitled org, but `/api/v1/scan-schedules` carries no entitlement and no feature flag
 * at all. Gating the section would hide a working page to hide a non-working one — the inverse of
 * the retired General section, where the only gated tab once carried the section.
 *
 * **Auto-scan after import** (2026-09-15, the third tab) is always available. Like its two siblings
 * it is member-readable (the effective policy is something a
 * developer importing a repository is entitled to know) and admin-only on every write — but unlike
 * Scheduled scans that admin gate is unconditional, not behind `phx.policy.require-admin-for-writes`.
 */
export const SCAN_POLICY_SECTION: SettingsSectionConfig = {
  title: 'Scanning',
  icon: 'policy',
  description:
    'How repositories get scanned: pull-request and hook policy, recurring scans, and what runs ' +
    'automatically after a first import.',
  tabs: [
    { label: 'PR Policy', route: '/settings/scan-policy/pr-policy', entitlement: 'policies' },
    {
      label: 'Hook policy',
      route: '/settings/scan-policy/hook-policy',
      entitlement: 'enforcementSessionGate',
      permissions: { allOf: ['enforcement.read'] },
    },
    { label: 'Scheduled scans', route: '/settings/scan-policy/scheduled-scans' },
    {
      label: 'Initial scans',
      route: '/settings/scan-policy/auto-scan',
    },
  ],
};
